import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-[#060C1A]" aria-label="Welcome to Ambient Fingerprinting" data-id="element-81">
      {/* Premium Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true" data-id="element-82">
        {/* Subtle Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1E1F55_1px,transparent_1px),linear-gradient(to_bottom,#1E1F55_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20" data-id="element-83" />

        {/* Glowing Orbs */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#4AA6CF] rounded-full mix-blend-screen filter blur-[150px] opacity-20 animate-pulse" style={{
        animationDuration: '4s'
      }} data-id="element-84" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-600 rounded-full mix-blend-screen filter blur-[150px] opacity-10 animate-pulse" style={{
        animationDuration: '6s'
      }} data-id="element-85" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-12 lg:py-0" data-id="element-86">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-8 items-center" data-id="element-87">
          {/* Left Content - Typography & CTAs */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="text-white max-w-2xl" data-id="element-88">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8" data-id="element-89">
              <ShieldCheckIcon className="h-4 w-4 text-[#4AA6CF]" data-id="element-90" />
              <span className="text-sm font-medium text-white/90" data-id="element-91">
                Trusted by Government Agencies & Employers
              </span>
            </motion.div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] mb-6 tracking-tight" data-id="element-92">
              Secure & Accurate <br data-id="element-93" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4AA6CF] via-[#6dbce0] to-purple-400" data-id="element-94">
                Live Scan Fingerprinting
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-white/80 mb-10 leading-relaxed font-light max-w-xl" data-id="element-95">
              Professional fingerprinting services tailored for your needs.
              Fast, reliable, and certified processing with state-of-the-art
              technology.
            </p>

            <div className="flex flex-col sm:flex-row gap-5 items-start sm:items-center" data-id="element-96">
              <a href="#/booking" className="relative group overflow-hidden rounded-full p-[1px] w-full sm:w-auto" data-id="element-97">
                <span className="absolute inset-0 bg-gradient-to-r from-[#4AA6CF] to-purple-500 rounded-full opacity-70 group-hover:opacity-100 transition-opacity duration-300" data-id="element-98" />
                <div className="relative bg-[#060C1A] group-hover:bg-opacity-0 transition-colors duration-300 px-8 py-3.5 rounded-full flex items-center justify-center gap-2" data-id="element-99">
                  <span className="font-semibold text-white" data-id="element-100">
                    Book Appointment
                  </span>
                  <ArrowRightIcon className="h-4 w-4 text-white group-hover:translate-x-1 transition-transform" data-id="element-101" />
                </div>
              </a>

              <div className="flex items-center gap-6 text-sm text-white/80 ml-2 sm:ml-4" data-id="element-102">
                <div className="flex items-center gap-2" data-id="element-103">
                  <CheckCircleIcon className="h-4 w-4 text-[#4AA6CF]" aria-hidden="true" data-id="element-104" />
                  <span data-id="element-105">Certified</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-106">
                  <CheckCircleIcon className="h-4 w-4 text-[#4AA6CF]" aria-hidden="true" data-id="element-107" />
                  <span data-id="element-108">Efficient</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Biometric Scanner Graphic */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.3,
          ease: 'easeOut'
        }} className="relative flex justify-center items-center lg:justify-end" aria-hidden="true" data-id="element-109">
            {/* Floating Badges */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-6 -left-4 lg:left-10 z-20 bg-[#0A0F1E]/80 backdrop-blur-md border border-white/10 px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-3" data-id="element-110">
              <div className="bg-purple-500/20 p-2 rounded-lg" data-id="element-111">
                <ShieldCheckIcon className="h-5 w-5 text-purple-400" data-id="element-112" />
              </div>
              <div data-id="element-113">
                <p className="text-xs text-white/80 font-medium" data-id="element-114">Compliance</p>
                <p className="text-sm font-bold text-white" data-id="element-115">FBI Approved</p>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -bottom-6 -right-4 lg:-right-4 z-20 bg-[#0A0F1E]/80 backdrop-blur-md border border-white/10 px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-3" data-id="element-116">
              <div className="bg-[#4AA6CF]/20 p-2 rounded-lg" data-id="element-117">
                <ClockIcon className="h-5 w-5 text-[#4AA6CF]" data-id="element-118" />
              </div>
              <div data-id="element-119">
                <p className="text-xs text-white/80 font-medium" data-id="element-120">Speed</p>
                <p className="text-sm font-bold text-white" data-id="element-121">15 Min Process</p>
              </div>
            </motion.div>

            {/* Main Scanner Container */}
            <div className="relative w-80 h-96 sm:w-96 sm:h-[450px] bg-gradient-to-b from-white/5 to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl shadow-[0_0_40px_rgba(74,166,207,0.1)] overflow-hidden flex items-center justify-center" data-id="element-122">
              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#4AA6CF]/50 rounded-tl-3xl m-4" data-id="element-123" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-[#4AA6CF]/50 rounded-tr-3xl m-4" data-id="element-124" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-[#4AA6CF]/50 rounded-bl-3xl m-4" data-id="element-125" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#4AA6CF]/50 rounded-br-3xl m-4" data-id="element-126" />

              {/* Fingerprint SVG */}
              <div className="relative w-48 h-64 sm:w-56 sm:h-72 opacity-80" data-id="element-127">
                <svg viewBox="0 0 200 250" className="w-full h-full text-[#4AA6CF]" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" data-id="element-128">
                  <path d="M100 40 C70 40, 50 60, 50 90 C50 130, 80 160, 100 200 C120 160, 150 130, 150 90 C150 60, 130 40, 100 40 Z" className="opacity-30" data-id="element-129" />
                  <path d="M100 60 C80 60, 65 75, 65 95 C65 125, 85 150, 100 180 C115 150, 135 125, 135 95 C135 75, 120 60, 100 60 Z" className="opacity-50" data-id="element-130" />
                  <path d="M100 80 C90 80, 80 90, 80 105 C80 125, 95 140, 100 160 C105 140, 120 125, 120 105 C120 90, 110 80, 100 80 Z" className="opacity-70" data-id="element-131" />
                  <path d="M100 100 C95 100, 90 105, 90 115 C90 125, 95 130, 100 140 C105 130, 110 125, 110 115 C110 105, 105 100, 100 100 Z" className="opacity-90" data-id="element-132" />

                  {/* Additional intricate lines */}
                  <path d="M60 110 C60 140, 85 170, 100 210" className="opacity-40" data-id="element-133" />
                  <path d="M140 110 C140 140, 115 170, 100 210" className="opacity-40" data-id="element-134" />
                  <path d="M70 105 C70 130, 90 155, 100 190" className="opacity-60" data-id="element-135" />
                  <path d="M130 105 C130 130, 110 155, 100 190" className="opacity-60" data-id="element-136" />
                </svg>

                {/* Animated Laser Scan Line */}
                <motion.div animate={{
                top: ['0%', '100%', '0%']
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute left-0 right-0 h-0.5 bg-[#4AA6CF] shadow-[0_0_20px_#4AA6CF] z-10" data-id="element-137">
                  <div className="absolute inset-0 bg-gradient-to-b from-[#4AA6CF]/40 to-transparent h-12 -top-12 pointer-events-none" data-id="element-138" />
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}